/**
 * Rate Limit Approval Component
 *
 * Inline approval UI when rate limits are hit during tool execution.
 * Shows blocked tool calls and allows user to approve or skip.
 */

import React from 'react';
import { AlertTriangle, Plus, Check, X } from 'lucide-react';
import type { ToolCall } from '../../hooks/useWorkflowChat';

interface RateLimitApprovalProps {
  allowedCount: number;
  blockedToolCalls: ToolCall[];
  resourceType: 'node' | 'edge';
  onAllow: () => void;
  onSkip: () => void;
}

export function RateLimitApproval({
  allowedCount,
  blockedToolCalls,
  resourceType,
  onAllow,
  onSkip,
}: RateLimitApprovalProps) {
  const resourceLabel = resourceType === 'node' ? 'nodes' : 'edges';
  const blockedCount = blockedToolCalls.length;

  return (
    <div className="bg-surface-800 border border-warning-500/30 rounded-lg p-3 my-2">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-warning-400" />
        <span className="text-sm text-text-primary font-medium">
          Rate Limit Reached
        </span>
      </div>

      <p className="text-sm text-text-secondary mb-3">
        Created {allowedCount} {resourceLabel}. {blockedCount} more requested:
      </p>

      <ul className="text-sm text-text-muted mb-3 space-y-1 max-h-32 overflow-y-auto">
        {blockedToolCalls.map((tc) => {
          const name = (tc.input as { name?: string }).name ||
                       (tc.input as { from_node_id?: string; to_node_id?: string }).from_node_id;
          return (
            <li key={tc.id} className="flex items-center gap-2">
              <Plus className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">"{name}"</span>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center gap-2">
        <button
          onClick={onAllow}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-success-500/20 hover:bg-success-500/30 text-success-400 rounded text-sm font-medium transition-colors"
        >
          <Check className="w-3.5 h-3.5" />
          Allow More
        </button>
        <button
          onClick={onSkip}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-700 hover:bg-surface-600 text-text-muted rounded text-sm font-medium transition-colors"
        >
          <X className="w-3.5 h-3.5" />
          Skip
        </button>
      </div>
    </div>
  );
}
